import React, { useState, useContext } from 'react';
import { View, ScrollView, StyleSheet, Alert, Platform } from 'react-native';
import { TextInput, Button, Text, SegmentedButtons } from 'react-native-paper';
// DateTimePicker only works on native platforms
let DateTimePicker: any;
if (Platform.OS !== 'web') {
  DateTimePicker = require('@react-native-community/datetimepicker').default;
}
import { useNavigation } from '@react-navigation/native';
import { FirebaseTripsContext } from '../context/FirebaseTripsContext';
import EmailImportScreen from './EmailImportScreen';
import { Trip } from '../types';

export default function AddTripScreen() {
  const navigation = useNavigation();
  const tripsContext = useContext(FirebaseTripsContext);
  
  const [mode, setMode] = useState<'manual' | 'parse'>('manual');
  const [tripName, setTripName] = useState('');
  const [destination, setDestination] = useState('');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);

  const handleManualSubmit = async () => {
    if (!tripName || !destination || !tripsContext) {
      Alert.alert('Error', 'Please fill in all fields');
      return;
    }

    try {
      const newTrip: Omit<Trip, 'id' | 'createdAt' | 'updatedAt'> = {
        name: tripName,
        destination,
        startDate,
        endDate,
        itinerary: [],
        collaborators: [],
      };

      await tripsContext.createTrip(newTrip);
      navigation.goBack();
    } catch (error) {
      Alert.alert('Error', 'Failed to add trip');
    }
  };

  return (
    <ScrollView style={styles.container}>
      <SegmentedButtons
        value={mode}
        onValueChange={setMode as any}
        buttons={[
          { value: 'manual', label: 'Manual Entry' },
          { value: 'parse', label: 'Parse Email' },
        ]}
        style={styles.segmentedButtons}
      />

      {mode === 'manual' ? (
        <View style={styles.formContainer}>
          <TextInput
            label="Trip Name"
            value={tripName}
            onChangeText={setTripName}
            mode="outlined"
            style={styles.input}
          />
          
          <TextInput
            label="Destination"
            value={destination}
            onChangeText={setDestination}
            mode="outlined"
            style={styles.input}
          />

          <View style={styles.dateContainer}>
            <Button
              mode="outlined"
              onPress={() => setShowStartPicker(true)}
              style={styles.dateButton}
            >
              Start: {startDate.toLocaleDateString()}
            </Button>
            
            <Button
              mode="outlined"
              onPress={() => setShowEndPicker(true)}
              style={styles.dateButton}
            >
              End: {endDate.toLocaleDateString()}
            </Button>
          </View>

          {showStartPicker && Platform.OS !== 'web' && DateTimePicker && (
            <DateTimePicker
              value={startDate}
              mode="date"
              onChange={(event: any, date?: Date) => {
                setShowStartPicker(false);
                if (date) setStartDate(date);
              }}
            />
          )}

          {showEndPicker && Platform.OS !== 'web' && DateTimePicker && (
            <DateTimePicker
              value={endDate}
              mode="date"
              onChange={(event: any, date?: Date) => {
                setShowEndPicker(false);
                if (date) setEndDate(date);
              }}
            />
          )}

          {/* Web date inputs */}
          {showStartPicker && Platform.OS === 'web' && (
            <View style={styles.webDatePicker}>
              <TextInput
                label="Start Date"
                value={startDate.toISOString().split('T')[0]}
                onChangeText={(text) => {
                  const date = new Date(text);
                  if (!isNaN(date.getTime())) {
                    setStartDate(date);
                  }
                }}
                mode="outlined"
                style={styles.input}
                placeholder="YYYY-MM-DD"
              />
              <Button onPress={() => setShowStartPicker(false)}>Done</Button>
            </View>
          )}

          {showEndPicker && Platform.OS === 'web' && (
            <View style={styles.webDatePicker}>
              <TextInput
                label="End Date"
                value={endDate.toISOString().split('T')[0]}
                onChangeText={(text) => {
                  const date = new Date(text);
                  if (!isNaN(date.getTime())) {
                    setEndDate(date);
                  }
                }}
                mode="outlined"
                style={styles.input}
                placeholder="YYYY-MM-DD"
              />
              <Button onPress={() => setShowEndPicker(false)}>Done</Button>
            </View>
          )}

          <Button
            mode="contained"
            onPress={handleManualSubmit}
            style={styles.submitButton}
          >
            Create Trip
          </Button>
        </View>
      ) : (
        <EmailImportScreen />
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  segmentedButtons: {
    margin: 16,
  },
  formContainer: {
    padding: 16,
  },
  input: {
    marginBottom: 16,
  },
  dateContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  dateButton: {
    flex: 0.48,
  },
  submitButton: {
    marginTop: 16,
  },
  instructions: {
    fontSize: 16,
    marginBottom: 16,
    textAlign: 'center',
    color: '#666',
  },
  emailInput: {
    marginBottom: 16,
    minHeight: 200,
  },
  webDatePicker: {
    marginBottom: 16,
  },
});